import type {
	APAADMINBaseModelsDeploymentBulkResult,
	APAADMINBaseModelsDeploymentAnalysisIssue,
	APAADMINBaseModelsDeploymentPageAnalysis,
	APAADMINBaseModelsPageOutOfSyncItem,
	DeploymentQueueItem
} from "../../API/Models";

/** Server response item for GET /api/deployment/page/out-of-sync */
export type PageOutOfSyncItem = { id: string; slug: string; language: string; title?: string | null; pendingReason?: string | null };
export const PAGE_CONTENT_TYPE = "page";
export const DEFAULT_LANGUAGE = "fr";

export const normalizeOutOfSyncItem = (item: APAADMINBaseModelsPageOutOfSyncItem): PageOutOfSyncItem => ({
	id: item.id ?? "",
	slug: item.slug ?? "",
	language: item.language ?? DEFAULT_LANGUAGE,
	title: item.title ?? null,
	pendingReason: item.pendingReason ?? null
});

/** Analysis issue: red = blocks production deploy, orange = warning */
export type DeploymentAnalysisIssue = {
	slug: string;
	language: string;
	severity: "error" | "warning";
	code: string;
	message: string;
};

/** Per-page analysis result for "details for every content-page" */
export type DeploymentPageAnalysis = {
	id: string | null;
	slug: string;
	language: string;
	title: string | null;
	issues: DeploymentAnalysisIssue[];
};

export type DeploymentBulkResult = {
	total: number;
	succeeded: number;
	failed: number;
	items: Array<{ slug: string; language: string; success: boolean; error?: string | null }>;
};

export const normalizeIssue = (issue: APAADMINBaseModelsDeploymentAnalysisIssue): DeploymentAnalysisIssue => ({
	slug: String(issue.slug ?? ""),
	language: String(issue.language ?? DEFAULT_LANGUAGE),
	severity: issue.severity === "error" ? "error" : "warning",
	code: String(issue.code ?? ""),
	message: String(issue.message ?? "")
});

export const normalizeAnalysis = (item: APAADMINBaseModelsDeploymentPageAnalysis): DeploymentPageAnalysis => ({
	id: item.id ?? null,
	slug: String(item.slug ?? ""),
	language: String(item.language ?? DEFAULT_LANGUAGE),
	title: item.title ?? null,
	issues: Array.isArray(item.issues) ? item.issues.map((issue) => normalizeIssue(issue)) : []
});

export const normalizeDeployResult = (result?: APAADMINBaseModelsDeploymentBulkResult | null): DeploymentBulkResult => ({
	total: result?.total ?? 0,
	succeeded: result?.succeeded ?? 0,
	failed: result?.failed ?? 0,
	items: Array.isArray(result?.items)
		? result!.items!.map((item) => ({
				slug: String(item?.slug ?? ""),
				language: String(item?.language ?? DEFAULT_LANGUAGE),
				success: Boolean(item?.success),
				error: item?.error ?? null
		  }))
		: []
});

export const normalizeQueueItem = (item: DeploymentQueueItem): DeploymentQueueItem => ({
	id: item.id ?? null,
	target: item.target ?? null,
	status: item.status ?? null,
	contentType: item.contentType ?? null,
	language: item.language ?? null,
	slug: item.slug ?? null,
	contentId: item.contentId ?? null,
	pagePaths: Array.isArray(item.pagePaths) ? item.pagePaths : [],
	action: item.action ?? null,
	message: item.message ?? null,
	author: item.author ?? null,
	createdAt: item.createdAt,
	updatedAt: item.updatedAt,
	error: item.error ?? null
});

/** Only show View changes / Revert for pages that are actually pending (in the out-of-sync list). */
export const isPending = (pa: DeploymentPageAnalysis, pendingItems: PageOutOfSyncItem[]) =>
	pendingItems.some((i) => i.slug === pa.slug && i.language === pa.language);
